import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useT, Namespace } from "@racoon/i18n";
import { Button } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import { IcPrint } from "@racoon/icons";
import { TruckModal } from "./truck.modal";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import {
  useGetOrganizationSettingsQuery,
  useGetOrganizationByIdQuery,
} from "~/shared/api/organization/organization.grpc";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { isShiftTerminal, resolveShiftReleasedAt } from "../helpers";
import { printWaybills, type ShiftData as PrintShiftData } from "../helpers/print-waybill.helper";

export const CarsAdditionalButtons: React.FC = () => {
  const t = useT();
  const { activeCompanyId, activeCompany: organization } = useAuthSession();
  const company_id = activeCompanyId ?? undefined;
  const [sp] = useSearchParams();
  const dateParam = sp.get("date");

  const selectedDate = useMemo(() => {
    if (dateParam) {
      const [y, m, d] = dateParam.split("-").map(Number);
      if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
        return new Date(y, m - 1, d);
      }
    }
    return new Date();
  }, [dateParam]);

  const startOfDay = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return toTimestamp(d);
  }, [selectedDate]);

  const endOfDay = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(23, 59, 59, 999);
    return toTimestamp(d);
  }, [selectedDate]);

  // Fetch organization settings & data
  const { data: organizationSettings, refetch: refetchSettings } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !activeCompanyId,
    },
  );

  const { data: organizationData } = useGetOrganizationByIdQuery(
    { organizationId: BigInt(activeCompanyId!) },
    { skip: !activeCompanyId },
  );

  const { data: trucksData } = useListTrucksQuery(
    { ownerOrganizationIds: company_id ? [BigInt(company_id)] : [] },
    { skip: !company_id },
  );

  const { data: shiftData } = useListTruckDriverOnShiftsQuery(
    {
      ownerOrganizationId: company_id ? BigInt(company_id) : 0n,
      datetimeFilter: { from: startOfDay, to: endOfDay },
    },
    { skip: !company_id },
  );

  const activeShiftsToPrint = useMemo(() => {
    const list = trucksData?.trucks || [];
    const shiftsMap = new Map<bigint, ConnectProto.DriverOnShift[]>();
    if (shiftData?.truckDriverOnShifts) {
      for (const ts of shiftData.truckDriverOnShifts) {
        if (ts.truck?.id && ts.driverOnShifts) {
          shiftsMap.set(ts.truck.id, ts.driverOnShifts);
        }
      }
    }

    const result: PrintShiftData[] = [];
    for (const truck of list) {
      const truckShifts = shiftsMap.get(truck.id) || [];
      // Running and planned shifts both get a waybill — the driver takes it
      // before the start; a shift with no driver yet has no one to print it for.
      for (const shift of truckShifts) {
        if (isShiftTerminal(shift.status) || !shift.driver) continue;
        const driver = shift.driver;
        const releasedAt = resolveShiftReleasedAt(shift);
        const completedAt = fromTimestamp(shift.completedAt)?.toISOString();

        result.push({
          id: String(shift.id),
          status: shift.status,
          plateNumber: truck.plateNumber || "",
          driverName:
            `${driver.firstName || ""} ${driver.lastName || ""} №${driver.driverLicenseNumber || ""}`.trim(),
          type: String(truck.truckType?.type || truck.type || ""),
          model: truck.model || "",
          trailer: truck.techPassport || "",
          releasedAt,
          twStartAt: fromTimestamp(shift.twStartAt)?.toISOString(),
          mileageBefore: Number(shift.odometerReading ?? 0),
          acceptedAt: completedAt,
          completed_at: completedAt,
        });
      }
    }
    return result;
  }, [trucksData?.trucks, shiftData?.truckDriverOnShifts]);

  const handlePrintWaybill = async () => {
    if (!activeShiftsToPrint.length) return;

    // Refresh settings
    const { data: freshSettings } = await refetchSettings();

    await printWaybills(activeShiftsToPrint, {
      organization: organizationData || organization || undefined,
      organizationSettings: freshSettings || organizationSettings || undefined,
      t,
    });
  };

  return (
    <div className="flex gap-2 items-center">
      <TruckModal />
      <Button
        id="cars-tab-print-waybill-button"
        size="sm"
        variant="opacityBlueWithoutOutline"
        onClick={handlePrintWaybill}
        className="gap-2 flex-auto md:flex-initial"
        disabled={activeShiftsToPrint.length === 0}
      >
        <IcPrint className="h-4 w-4" />
        {t(Namespace.Autopark, "shifts.printWaybill")}
        {activeShiftsToPrint.length > 0 && ` (${activeShiftsToPrint.length})`}
      </Button>
    </div>
  );
};
